import type { ReactNode } from "react"
import { useEffect, useState } from "react"
import type { BasketPatchItem, PeriodName, Price } from "@onestore/api/types"
import type { ButtonAtmProps } from "@onestore/hel/dist/components/atoms/ButtonAtm"
import NumberInputMol from "@onestore/hel/dist/components/molecules/NumberInputMol"
import {
  ParameterItem,
  ParametersGroup,
} from "@onestore/hel/dist/components/molecules/PackageBoxSlimMol/PackageBoxSlimMol"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import type Theme from "@onestore/hel/dist/typings/theme"
import type { ResponsiveBackgroundImage } from "@onestore/hel/dist/typings/types"
import _t from "@core/i18n"
import { getSelectedPeriodInfo, PeriodInfo } from "@core/period-info"
import { formatPriceObject } from "@core/pricing"
import type { PriceWithOptionalTaxRate } from "@core/types"
import { PriceFormat, PriceType } from "@core/types"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import { getDetailedPriceData } from "@gatsby-plugin-generic-page/components/DetailedPrice/helpers/getDetailedPriceData"
import { getFirstPurchaseFreePriceData } from "@gatsby-plugin-generic-page/components/DetailedPrice/helpers/getFirstPurchaseFreePriceData"
import ProductBoxFooter from "@gatsby-plugin-generic-page/components/ProductBoxSlim/ProductBoxFooter"
import type { BackgroundImages } from "@gatsby-plugin-generic-page/fragments/backgroundImages"
import type { BadgeUdsContent } from "@gatsby-plugin-generic-page/fragments/badgeUds"
import type { CustomTextColors } from "@gatsby-plugin-generic-page/fragments/customColor"
import type {
  UdsButtonConcept,
  UdsButtonVariant,
} from "@gatsby-plugin-generic-page/fragments/udsButton"
import getBackground from "@gatsby-plugin-generic-page/helpers/getBackground"
import { getDomainsPoolId } from "@gatsby-plugin-generic-page/helpers/getDomainsPoolId"
import { getMarkdownUdsBadge } from "@gatsby-plugin-generic-page/helpers/getMarkdownUdsBadge"
import { stripMarkdownText } from "@gatsby-plugin-generic-page/helpers/stripMarkdownText"
import useProductPeriodInfo from "@gatsby-plugin-generic-page/hooks/useProductPeriodInfo"
import type { PriceData } from "@ionos-web-design-system/react"
import { first, last } from "lodash"
import PacketSpecialDiscountDrawer from "~/components/PacketSpecialDiscountDrawer"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { ProductBoxBundle } from "~/fragments/bundle"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import useNumberInputMolState from "~/hooks/useNumberInputMolState"
import { productBoxClickEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import onestoreVars from "~/lib/onestoreVars"
import {
  getBundledProduct,
  getMainProduct,
  getSelectedPeriod,
} from "~/lib/plan"
import type { PlanSettings } from "~/lib/url"
import url from "~/lib/url"
import useCloudbluePlanTooltip from "./useCloudbluePlanTooltip"

export type ProductBoxSlimContent = {
  parameters?: ReactNode
  footer?: {
    value?: string
    label: string
    isBundle?: boolean
    tooltip?: ReactNode
  }
}

type Box = {
  selectedPlan: CloudBluePlan["flatData"]
  title: string
  description?: string
  bundleText?: string
  promoText?: string
  priceData?: PriceData
  firstPurchaseFreePriceData?: PriceData
  button: Omit<ButtonAtmProps, "size" | "isWider" | "isExpanded">
  parameters?: ReactNode
  footer?: ReactNode
  outerFooter?: ReactNode
  isTrialBox?: boolean
  customTextColor?: CustomTextColors
  backgroundImage?: ResponsiveBackgroundImage
  onChangePeriodModalClick?: () => void
  isBundleChecked?: boolean
  /** Current value of the plan-input counter, i.e. the `planInputValue` of the
      sale configuration the box is showing right now. 0 for boxes without a
      counter. */
  selectedPlanInputValue: number
  additionalButton?: ReactNode
}

export type ProductBoxSlimHookProps = {
  isBundleDefaultChecked?: boolean
  promoText?: string
  description?: string
  content: ProductBoxSlimContent
  outerFooter?: ReactNode
  plan: CloudBluePlan["flatData"] | Record<number, CloudBluePlan["flatData"]>
  period?: PeriodName
  code?: string
  title: string
  discountLabel?: string
  labels?: BadgeUdsContent[]
  upsell?: (string | PlanSettings)[]
  bundle?: CloudBluePlan["flatData"][]
  upgrade?: string
  bundledTitle?: string
  priceType?: PriceType
  concept: UdsButtonConcept
  variant: UdsButtonVariant
  buttonText?: string
  hideRenewalPrices?: boolean
  additionalText?: string
  hasChangedDefaultPlan?: boolean
  planInputLabel?: string
  index?: number
  saleConfiguration?: SaleConfiguration[]
  bundlePlan?: ProductBoxBundle[] | null
  changePeriod?: PeriodName
  backgroundImage?: BackgroundImages
  customTextColor?: Theme.CustomColor
  isTrialBox?: boolean
}

export const calculateBundlePrice = (
  bundlePrice: PriceWithOptionalTaxRate,
  productPrice: PriceWithOptionalTaxRate
): PriceWithOptionalTaxRate =>
  bundlePrice &&
  productPrice && {
    ...bundlePrice,
    netto: bundlePrice.netto - productPrice.netto,
    gross: bundlePrice.gross - productPrice.gross,
  }

export default function useProductBoxSlim({
  isBundleDefaultChecked = false,
  promoText,
  description,
  plan,
  period,
  content,
  code,
  title = "",
  discountLabel,
  labels,
  upsell = [],
  bundle = [],
  upgrade,
  bundledTitle,
  priceType: initialPriceType,
  buttonText,
  outerFooter,
  hideRenewalPrices: initialHideRenewalPrices,
  additionalText,
  hasChangedDefaultPlan,
  planInputLabel,
  index,
  saleConfiguration,
  bundlePlan,
  changePeriod,
  isTrialBox,
  backgroundImage,
}: ProductBoxSlimHookProps): Box {
  const { currentPriceType, isNettoPriceType } = usePriceTypeContext()
  const priceType = currentPriceType || initialPriceType
  const { hideRenewalPrices } = onestoreVars.flags
  const plans: Record<number, CloudBluePlan["flatData"]> =
    "alias" in plan ? { 0: plan } : plan
  const [checked, setChecked] = useState<boolean>(isBundleDefaultChecked)
  const quantities = Object.keys(plans).map((int) => parseInt(int, 10))

  const { props: numberInputProps, currentValue: selectedQuantity } =
    useNumberInputMolState(
      first(quantities) || 0,
      last(quantities) || 0,
      quantities[0] || 0
    )

  const handleSetIsChecked = () => {
    setChecked((v) => !v)
  }

  const bundleProduct = getBundledProduct(bundle)
  const selectedBundlePlan = checked && bundle.length > 1 ? bundleProduct : null

  const selectedPlan: CloudBluePlan["flatData"] = checked
    ? getMainProduct(plans[selectedQuantity], bundle)
    : plans[selectedQuantity]

  let bundlePrice: Price | null = null
  let productPrice: Price | null = null

  if (bundle && bundleProduct) {
    const productPeriodInfo = getSelectedPeriodInfo(
      selectedPlan.periods,
      undefined
    )

    const bundlePeriodInfo = getSelectedPeriodInfo(
      bundleProduct.periods,
      undefined
    )

    bundlePrice = bundlePeriodInfo
      ? bundlePeriodInfo.getRegisterPriceValue()
      : null
    productPrice = productPeriodInfo
      ? productPeriodInfo.getRegisterPriceValue()
      : null

    if (bundleProduct.id && bundlePeriodInfo) {
      const bundleSpecialPrice = bundlePeriodInfo.getPromoPriceForPlanPrice(
        selectedPlan.id
      )

      if (bundleSpecialPrice) {
        bundlePrice = bundleSpecialPrice
      }
    }
  }

  const [mainProductPrice, setMainProductPrice] =
    useState<PriceWithOptionalTaxRate | null>(productPrice)

  useEffect(() => {
    setMainProductPrice(productPrice)
  }, [])

  let bundledProduct: typeof selectedBundlePlan | undefined
  let bundlePriceFormat: ReturnType<typeof formatPriceObject> | undefined

  if (
    hasChangedDefaultPlan &&
    bundlePrice &&
    productPrice &&
    mainProductPrice
  ) {
    const calculatedBundlePrice = calculateBundlePrice(
      checked ? productPrice : bundlePrice,
      mainProductPrice
    )

    bundlePriceFormat =
      calculatedBundlePrice &&
      formatPriceObject(calculatedBundlePrice, priceType)
  } else if (bundlePrice) {
    bundlePriceFormat = formatPriceObject(bundlePrice, priceType)
  }

  if (checked && selectedBundlePlan) {
    bundledProduct = selectedBundlePlan
  }

  const { periodInfo, cloudBluePeriod, bundlePeriod, planID } =
    useProductPeriodInfo(checked, saleConfiguration, bundlePlan, period)

  const { limitTooltip } = useCloudbluePlanTooltip(planID)

  const isOneTimeFee = periodInfo ? periodInfo.isOneTimeFee() : null
  const isMultiPlan = !("alias" in plan)

  const priceFormat = periodInfo
    ? periodInfo.getFormatted(
        isNettoPriceType ? PriceFormat.NETTO : PriceFormat.GROSS
      )
    : undefined

  //price
  function getPricingData(
    product,
    bundledProduct,
    periodName: PeriodName | undefined
  ) {
    const selectedPeriod = getSelectedPeriod(product, periodName)

    let bundledPeriod = bundledProduct
      ? getSelectedPeriod(bundledProduct, periodName)
      : undefined

    // Hack na promocje nadrzędną dla hosting + ssl (ONESTORE-4684)
    if (bundledProduct && bundledPeriod) {
      const bundledPrice =
        bundledPeriod.parent_promotions.find(
          (item) => item.parent_plan_id === product.id
        ) ?? null

      if (bundledPrice !== null) {
        bundledPeriod = {
          ...bundledPeriod,
          price_register: bundledPrice.price_register,
        }
      }
    }

    return new PeriodInfo(selectedPeriod, bundledPeriod)
  }

  const [changedPrice, setPrice] = useState(
    getPricingData(selectedPlan, bundledProduct, period)
  )

  const productId = selectedPlan.id
  const bundledProductId = bundledProduct?.id

  useEffect(() => {
    setPrice(getPricingData(selectedPlan, bundledProduct, period))
  }, [period, bundledProduct, selectedPlan])

  const item: BasketPatchItem = {
    plan: productId,
    quantity: 1,
  }

  if (bundledProductId) {
    item.children = [
      {
        plan: bundledProductId,
        quantity: 1,
      },
    ]
  }

  const periodInfoValues = periodInfo
    ? periodInfo.getPrices(priceType)
    : {
        priceValue: "",
        lowestPrice: "",
        lowestPricePercent: "",
        renewPrice: "",
      }

  const changedPeriodInfoValues = changedPrice
    ? changedPrice.getPrices(priceType)
    : {
        priceValue: "",
        lowestPrice: "",
        lowestPricePercent: "",
        renewPrice: "",
      }

  const { renewPrice } =
    !isMultiPlan && !hasChangedDefaultPlan
      ? periodInfoValues
      : changedPeriodInfoValues

  const updatedPeriodInfo =
    !isMultiPlan && !hasChangedDefaultPlan ? periodInfo : changedPrice

  const hasPromotion = updatedPeriodInfo?.hasPromotion() ?? false

  const hasLimitTooltip = updatedPeriodInfo?.hasLimit() ?? false

  const href = url.generateBonusUrl({
    p: selectedPlan.alias,
    b: selectedBundlePlan?.alias || undefined,
    ax: upsell,
    u: upgrade,
    code: code,
    period: period,
    domainSearch: !isEmpty(saleConfiguration)
      ? getDomainsPoolId(saleConfiguration[0])
      : undefined,
    priceType: currentPriceType,
  })

  let hrefChangePeriod: string

  if (!isEmpty(changePeriod)) {
    hrefChangePeriod = url.generateBonusUrl({
      p: selectedPlan.alias,
      b: selectedBundlePlan?.alias || undefined,
      ax: upsell,
      u: upgrade,
      code: code,
      period: changePeriod,
      domainSearch: !isEmpty(saleConfiguration)
        ? getDomainsPoolId(saleConfiguration[0])
        : undefined,
      priceType: currentPriceType,
    })
  }

  /// renew price
  const productName = saleConfiguration
    ? saleConfiguration[0].flatData.name
    : null
  const bundledProductName = bundlePlan ? bundlePlan[0].flatData.name : null

  const productText = `${_t(
    `periodName.renewPrice.${periodInfo?.getPeriodType()}`,
    {
      smart_count: periodInfo?.getPeriodLength(),
    }
  )}`

  const formatedRenewPrice = renewPrice

  let priceRenewal: string | string[][] = `${_t(
    "prices.renewal"
  )}: ${formatedRenewPrice} ${priceFormat} ${productText}`

  if (checked && bundlePeriod?.period_name !== cloudBluePeriod?.period_name) {
    const productPeriodInfo = cloudBluePeriod
      ? new PeriodInfo(cloudBluePeriod)
      : null

    const productPeriodText = `${_t(
      `periodName.renewPrice.${productPeriodInfo?.getPeriodType()}`,
      {
        smart_count: productPeriodInfo?.getPeriodLength(),
      }
    )}`

    const bundlePeriodInfo = bundlePeriod ? new PeriodInfo(bundlePeriod) : null

    const bundleProductPeriodText = `${_t(
      `periodName.renewPrice.${bundlePeriodInfo?.getPeriodType()}`,
      {
        smart_count: bundlePeriodInfo?.getPeriodLength(),
      }
    )}`

    priceRenewal = []

    if (productPeriodInfo?.hasRenewalPrice()) {
      const priceRenewalValue =
        productPeriodInfo?.getFormatted(
          priceType === PriceType.NETTO
            ? PriceFormat.RENEW_NETTO_NO_PERIOD
            : PriceFormat.RENEW_GROSS_NO_PERIOD
        ) ?? ""

      priceRenewal.push([
        `${_t(
          "prices.renewal"
        )} ${productName}: ${priceRenewalValue} ${priceFormat} ${productPeriodText}`,
      ])
    }

    if (bundlePeriodInfo?.hasRenewalPrice()) {
      const bundlePriceRenewalvalue =
        bundlePeriodInfo?.getFormatted(
          priceType === PriceType.NETTO
            ? PriceFormat.RENEW_NETTO_NO_PERIOD
            : PriceFormat.RENEW_GROSS_NO_PERIOD
        ) ?? ""

      priceRenewal.push([
        `${_t(
          "prices.renewal"
        )} ${bundledProductName}: ${bundlePriceRenewalvalue} ${priceFormat} ${bundleProductPeriodText}`,
      ])
    }
  }

  if (isOneTimeFee) {
    priceRenewal = _t("bonus.oneTimeFee")
  }

  const renewalPayment =
    !isTrialBox && !initialHideRenewalPrices && !hideRenewalPrices
      ? additionalText || !isEmpty(renewPrice)
        ? priceRenewal
        : undefined
      : undefined
  ///

  const currentPriceDataTestId =
    index !== undefined && index !== null
      ? `product-box-price-${index}`
      : "product-box-price"

  const detailedPriceData =
    !isEmpty(updatedPeriodInfo) && !isEmpty(priceType)
      ? getDetailedPriceData(
          priceType,
          updatedPeriodInfo,
          selectedPlan.priceInfo,
          Array.isArray(renewalPayment) ? renewalPayment.flat() : undefined,
          undefined,
          initialHideRenewalPrices,
          true
        )
      : undefined

  const priceData: PriceData | undefined = !isEmpty(detailedPriceData)
    ? {
        ...detailedPriceData,
        main: {
          ...detailedPriceData.main,
          testId: currentPriceDataTestId,
        },
      }
    : undefined

  const firstPurchaseFreePriceData = !isEmpty(updatedPeriodInfo)
    ? (getFirstPurchaseFreePriceData(priceType, updatedPeriodInfo) ?? undefined)
    : undefined

  const labelsMarkdownText = !isEmpty(labels)
    ? labels.map((label) => getMarkdownUdsBadge(label.flatData)).join(" ")
    : undefined

  const discountLabelText = [
    hasPromotion
      ? getMarkdownUdsBadge({
          text: _t("marketplace.discountTitle"),
          variant: "promo",
        })
      : !isEmpty(discountLabel)
        ? getMarkdownUdsBadge({ text: discountLabel, variant: "promo" })
        : null,
    hasLimitTooltip && !isEmpty(limitTooltip)
      ? `${getMarkdownUdsBadge({ text: _t("limitTooltip.title"), variant: "promo" })}[?](${limitTooltip})`
      : null,
  ]
    .filter(Boolean)
    .join(" ")

  const resolvedLabelsMarkdownText = [discountLabelText, labelsMarkdownText]
    .filter(Boolean)
    .join(" ")

  let headerTitleText = isEmpty(title) ? selectedPlan.name : title

  if (!isEmpty(resolvedLabelsMarkdownText)) {
    headerTitleText = `${resolvedLabelsMarkdownText}\n${headerTitleText}`
  }

  const bundleText = !isEmpty(bundledProduct)
    ? `[**+ ${bundledTitle || bundledProduct.name}**](promotion)`
    : undefined

  const button: Omit<ButtonAtmProps, "size" | "isWider" | "isExpanded"> = {
    text: buttonText || _t("marketplace.chooseButton"),
    title: buttonText || _t("marketplace.chooseButton"),
    rel: "nofollow",
    onClick: () => {
      document.location.href = href

      productBoxClickEvent({
        productbox_type: "button",
        productbox_option: stripMarkdownText(headerTitleText ?? ""),
        productbox_checked_item: planID || undefined,
        productbox_time: periodInfo?.period.period_name,
        productbox_package: href,
      })
      window.jentisDataLayer?.pushProductBoxClickEvent({
        productbox_type: "button",
        productbox_option: stripMarkdownText(headerTitleText ?? ""),
        productbox_checked_item: planID || undefined,
        productbox_time: periodInfo?.period.period_name,
        productbox_package: href,
      })
    },
  }

  const parameters = (
    <div>
      {content.parameters}

      {planInputLabel && !!numberInputProps && !!selectedQuantity ? (
        <ParametersGroup key={`parameter-group-${planInputLabel}`}>
          <ParameterItem
            label={planInputLabel}
            cta={
              <AlignContentOrg horizontalAlign="center">
                <NumberInputMol {...numberInputProps} />
              </AlignContentOrg>
            }
          />
        </ParametersGroup>
      ) : null}
    </div>
  )

  const footer = content?.footer ? (
    <ProductBoxFooter
      {...content.footer}
      bundlePriceFormat={bundlePriceFormat}
      setIsChecked={handleSetIsChecked}
      isChecked={checked}
      periodInfo={getSelectedPeriodInfo(selectedPlan.periods, undefined)}
    />
  ) : undefined

  const onChangePeriodModalClick = () => {
    document.location.href = hrefChangePeriod

    productBoxClickEvent({
      productbox_type: "button",
      productbox_option: stripMarkdownText(headerTitleText ?? ""),
      productbox_checked_item: planID || undefined,
      productbox_time: changePeriod,
      productbox_package: hrefChangePeriod,
    })
    window.jentisDataLayer?.pushProductBoxClickEvent({
      productbox_type: "button",
      productbox_option: stripMarkdownText(headerTitleText ?? ""),
      productbox_checked_item: planID || undefined,
      productbox_time: changePeriod,
      productbox_package: hrefChangePeriod,
    })
  }

  const { customOnColorText, imageBackground } = getBackground(backgroundImage)

  const packetSpecialDiscountDrawer = !isEmpty(saleConfiguration)
    ? saleConfiguration[0].flatData.defaultCloudBluePlan[0].flatData
        .packetSpecialDiscountDrawer
    : null

  const additionalButton = !isEmpty(packetSpecialDiscountDrawer) ? (
    <PacketSpecialDiscountDrawer {...packetSpecialDiscountDrawer} />
  ) : null

  return {
    selectedPlan,
    title: headerTitleText,
    description,
    bundleText,
    promoText,
    priceData,
    firstPurchaseFreePriceData,
    button,
    parameters,
    footer,
    outerFooter,
    customTextColor:
      isTrialBox && customOnColorText ? customOnColorText : undefined,
    backgroundImage:
      isTrialBox && imageBackground ? imageBackground : undefined,
    onChangePeriodModalClick,
    isTrialBox,
    isBundleChecked: checked,
    selectedPlanInputValue: selectedQuantity,
    additionalButton,
  }
}
